import * as React from 'react';
import { selector } from '@glitz/core';
import { styled } from '@glitz/react';
import { createTransitionDecorator } from '@avensia/excite-ui';
import { Input, createBorderDecorator } from './text-field';
import { createThemeDecorator, Theme, type ThemeType } from '../../theme';
import { createFieldSizeDecorator, useFieldSize, type FieldSizeType } from '../field-size';

type PropType = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className' | 'style' | 'size' | 'value' | 'defaultValue' | 'onChange' | 'onKeyDown' | 'onKeyUp'
> & {
  value: string | number | undefined;
  size?: FieldSizeType;
  as?: ThemeType;
  invalid?: boolean;
  keepBorder?: boolean; //Can be used to keep border when using transparent theme,
  hideSpinner?: boolean;
  prefix?: string; // Set prefix on input field
  suffix?: string; // Set suffix on input field
  delay?: number;
  inputmode?: string;
  ref?: React.Ref<HTMLInputElement>;
  onValueChange: (value: any) => void;
};

export const AdornedInput = styled(
  ({
    as = Theme.White,
    size,
    invalid,
    keepBorder,
    prefix,
    suffix,
    delay = 300,
    onValueChange,
    value,
    ...props
  }: PropType) => {
    const defaultSize = useFieldSize();
    const fieldSize = size ?? defaultSize;

    const decorator = React.useMemo(
      () =>
        styled(
          AdornedInputDecorator,
          createThemeDecorator(as),
          createBorderDecorator(invalid ? Theme.Negative : keepBorder ? Theme.White : as),
          createFieldSizeDecorator(fieldSize, false, true),
          invalid && invalidDecorator,
        ),
      [as, fieldSize, invalid, keepBorder],
    );

    const [fieldValue, setFieldValue] = React.useState(value);
    React.useEffect(() => {
      const timeoutID = setTimeout(() => onValueChange(fieldValue), delay);
      return () => clearTimeout(timeoutID);
    }, [fieldValue, onValueChange, delay]);

    React.useEffect(() => setFieldValue(value), [value]);

    return (
      <styled.Div css={decorator}>
        {prefix && (
          <AdornmentContainer>
            <Adornment>{prefix}</Adornment>
          </AdornmentContainer>
        )}

        <StyledInput value={fieldValue} onChange={e => setFieldValue(e.target.value)} {...props} />

        {suffix && (
          <AdornmentContainer>
            <Adornment>{suffix}</Adornment>
          </AdornmentContainer>
        )}
      </styled.Div>
    );
  },
);

const AdornedInputDecorator = styled(
  {
    color: t => t.facets?.count?.color,
    cursor: 'text',
    display: 'inline-flex',
    gap: 0,
    position: 'relative',
    alignItems: 'center',
  },
  // Element is first
  selector(':has(div:first-child)', {
    padding: {
      left: '4px',
    },
  }),
  // Element is last
  selector(':has(div:last-child)', {
    padding: {
      right: '4px',
    },
  }),
);

const StyledInput = styled(
  Input,
  {
    //fontSize: t => t.facets?.adornment?.input?.fontSize,
    color: t => t.facets?.adornment?.input?.color,
    width: '100%',
    margin: {
      bottom: 0,
    },
    padding: { xy: '8px' },
    display: 'inline-block',
    border: {
      xy: {
        color: t => t.facets?.adornment?.input?.color ?? 'inherit',
        style: 'inherit',
        width: 0,
      },
    },
    ':focus': {
      color: t => t.facets?.adornment?.input?.text ?? 'black',
    },
  },
  // Has Adornment Before
  selector(':not(:first-child)', {
    padding: {
      left: 0,
    },
  }),
  // Has Adornment After
  selector(':not(:last-child)', {
    padding: {
      right: 0,
    },
  }),
  createTransitionDecorator({ property: ['background', 'color', 'box-shadow'] }),
  // Hide spin buttons
  selector('[type=number]', {
    appearance: 'textfield',
    // Make sure to hide spin buttons on webkit based browsers
    ...selector(['::-webkit-outer-spin-button', '::-webkit-inner-spin-button'], {
      appearance: 'none',
      //margin: {
      //  xy: 0,
      //},
    }),
  }),
);

const AdornmentContainer = styled.div(
  {
    display: 'flex',
    height: '0.01em',
    maxHeight: '2em',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
  selector(':is(:first-child)', {
    margin: {
      right: '8px',
    },
  }),
  selector(':is(:last-child)', {
    margin: {
      left: '8px',
    },
  }),
);

const Adornment = styled.span({
  //fontSize: t => t.facets?.adornment?.affix?.fontSize,
  color: t => t.facets?.adornment?.affix?.color,
  display: 'flex',
  alignItems: 'center',
  padding: {
    x: '.375rem',
    y: '.75rem',
  },
  userSelect: 'none',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  margin: {
    bottom: 0,
  },
});

const invalidDecorator = styled({
  outlineStyle: 'solid',
  outlineColor: t => t.negative.color,
  outlineWidth: 1,
});
